import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Upload, FileText, Loader2, Wand2 } from 'lucide-react';
import { generateDocumentWithAI, type AIDocumentDraft } from '../../lib/documents';

interface AICreateModalProps {
  businessId: string;
  businessName?: string;
  isOpen: boolean;
  onClose: () => void;
  onGenerated: (draft: AIDocumentDraft) => void;
}

type Mode = 'prompt' | 'pdf';

const EXAMPLES = [
  'A daily vehicle safety inspection checklist for technicians',
  'A job-site hazard assessment form with sign-off',
  'An employee onboarding SOP with acknowledgement',
];

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AICreateModal({ businessId, businessName, isOpen, onClose, onGenerated }: AICreateModalProps) {
  const [mode, setMode] = useState<Mode>('prompt');
  const [prompt, setPrompt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      let draft: AIDocumentDraft;
      if (mode === 'prompt') {
        if (!prompt.trim()) { setError('Describe the document you want.'); setBusy(false); return; }
        draft = await generateDocumentWithAI({ businessId, businessName, mode: 'prompt', prompt: prompt.trim() });
      } else {
        if (!file) { setError('Choose a PDF or image to replicate.'); setBusy(false); return; }
        const fileBase64 = await readFileAsBase64(file);
        draft = await generateDocumentWithAI({ businessId, businessName, mode: 'pdf', fileBase64, fileMime: file.type, fileName: file.name });
      }
      if (!draft.blocks || draft.blocks.length === 0) {
        setError('The AI could not build a document from that. Try adding more detail or a clearer file.');
        setBusy(false);
        return;
      }
      onGenerated(draft);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong generating the document.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100100] bg-black/40 flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-violet-100"><Sparkles className="h-5 w-5 text-violet-600" /></div>
            <h2 className="text-[16px] font-semibold text-gray-900">Create with AI</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 px-5 pt-4">
          <button onClick={() => setMode('prompt')} className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-medium ${mode === 'prompt' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            <Wand2 className="h-4 w-4" /> Describe it
          </button>
          <button onClick={() => setMode('pdf')} className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-medium ${mode === 'pdf' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            <Upload className="h-4 w-4" /> Upload PDF
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {mode === 'prompt' ? (
            <>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                placeholder="Describe the document you want — e.g. 'A daily forklift safety checklist with a sign-off signature'."
                className="w-full text-[14px] border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
                style={{ fontSize: '16px' }}
              />
              <div className="mt-3 space-y-1.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Examples</div>
                {EXAMPLES.map((ex) => (
                  <button key={ex} onClick={() => setPrompt(ex)} className="block w-full text-left text-[13px] text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2">{ex}</button>
                ))}
              </div>
            </>
          ) : (
            <>
              <input ref={fileInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { setError(null); setFile(e.target.files?.[0] || null); }} />
              <button onClick={() => fileInputRef.current?.click()} className="w-full border-2 border-dashed border-gray-300 rounded-xl py-10 flex flex-col items-center justify-center text-gray-400 hover:border-violet-400 hover:text-violet-500 transition-colors">
                {file ? <FileText className="h-8 w-8 mb-2 text-violet-500" /> : <Upload className="h-8 w-8 mb-2" />}
                <span className="text-[13px] font-medium text-gray-700">{file ? file.name : 'Choose a PDF or image'}</span>
                <span className="text-[11px] text-gray-400 mt-0.5">{file ? 'Tap to replace' : 'AI will replicate it as an editable document'}</span>
              </button>
              <p className="text-[12px] text-gray-400 mt-3">Best with text-based PDFs or clear scans. You'll review & edit everything before saving.</p>
            </>
          )}

          {error && <div className="mt-3 text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button onClick={generate} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 disabled:opacity-60">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
